import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { showNotification } from '@epi-actions/overlays';
import emptyApi from '@epi-api/emptyApi';
import { del, get, post, put } from '@epi-helpers/FetchHelpers';
import config from '@epi-config';
import {
  BenefitGroupsResponse,
  IBenefitDefaultSettings,
  IBenefitDefaultSettingsResponse,
  IBenefitGroup,
  IBenefitGroupDetails,
  ICreateBenefitGroupForm
} from '@epi-models/pages/BenefitGroups';
import {
  GET_USER_DETAILS_API_TAG,
  CREATE_BENEFIT_GROUP_API_TAG,
  BENEFICIARY_TAGS,
  GET_BENEFIT_GROUP_DEFAULT_SETTINGS_API_TAG,
  GET_BENEFIT_GROUP_DETAILS_API_TAG
} from '@epi-api/apiTags';
import { CHANGE_BENEFICIARY_GROUP_API_TAG } from '@epi-constants/benefitGroups';

export const getBenefitGroups = async (): Promise<BenefitGroupsResponse> =>
  get<BenefitGroupsResponse>(`${config.apiHost}/api/v2/company-benefit-groups`);

export const deleteBenefitGroup = async (
  groupId: string
): Promise<{ data?: void; error?: any }> => {
  try {
    await del<void>(
      `${config.apiHost}/api/v2/company-benefit-groups/${groupId}`
    );
    return { data: undefined };
  } catch (e) {
    return { error: e };
  }
};

export const getDefaultBenefitGroupsSettings =
  async (): Promise<IBenefitDefaultSettingsResponse> =>
    get<IBenefitDefaultSettingsResponse>(
      `${config.apiHost}/api/v2/company-benefit-groups/defaultSettings`
    );

export const createBenefitGroup = async (
  payload: ICreateBenefitGroupForm
): Promise<{ data?: number; error?: any }> => {
  try {
    const response: number | undefined = await post(
      `${config.apiHost}/api/v2/company-benefit-groups`,
      payload
    );
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};
export const updateBenefitGroup = async (
  groupId: string,
  payload: ICreateBenefitGroupForm
): Promise<{ data?: void; error?: any }> => {
  try {
    const response = await put<void>(
      `${config.apiHost}/api/v2/company-benefit-groups/${groupId}`,
      payload
    );
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};
export const changeBeneficiaryGroup = async (
  personIds: string[],
  newGroupId: number
): Promise<{ data?: { affectedBeneficiaries: number }; error?: any }> => {
  try {
    const response: { affectedBeneficiaries: number } = await post<{
      affectedBeneficiaries: number;
    }>(`${config.apiHost}/api/company-beneficiaries/actions/change-group`, {
      personIds,
      newGroupId
    });
    return { data: { affectedBeneficiaries: response.affectedBeneficiaries } };
  } catch (e) {
    return { error: e };
  }
};

export const getBenfitGroupDetails = async (
  groupId: string
): Promise<{ data?: IBenefitGroupDetails; error?: any }> => {
  try {
    const response = await get<IBenefitGroupDetails>(
      `${config.apiHost}/api/v2/company-benefit-groups/${groupId}`
    );
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};

export const benefitGroupsApi = emptyApi.injectEndpoints({
  endpoints: build => ({
    getBenefitGroups: build.query<IBenefitGroup[] | [], void>({
      async queryFn(_arg, api) {
        try {
          const data = await getBenefitGroups();
          const items = data?.items.map(item => {
            const benefitSettings = Object.entries(item.benefitSettings).map(
              ([benefitType, { maxAmountPerPeriod }]) => ({
                benefitType,
                maxAmountPerPeriod
              })
            );

            return {
              ...item,
              benefitSettings
            };
          });
          return { data: items ?? [] };
        } catch (error) {
          api.dispatch(
            showNotification({ message: 'messages.error' }, 'error')
          );
          return { error: error as FetchBaseQueryError };
        }
      },
      keepUnusedDataFor: 1800,
      providesTags: [CREATE_BENEFIT_GROUP_API_TAG]
    }),
    getBenefitGroupDetails: build.query<IBenefitGroupDetails, string>({
      async queryFn(groupId, api) {
        try {
          const { data } = await getBenfitGroupDetails(groupId);

          const benefitSettings = data?.benefitSettings
            ? Object.entries(data?.benefitSettings).map(
                ([benefitType, values]: [string, any]) => ({
                  benefitType,
                  maxAmountPerPeriod: values.maxAmountPerPeriod,
                  recurringTopupRequestEnabled:
                    values.recurringTopupRequestEnabled
                })
              )
            : [];
          const result: IBenefitGroupDetails = {
            groupId: data?.groupId ?? 0,
            groupName: data?.groupName ?? '',
            benefitSettings
          };

          return { data: result };
        } catch (error) {
          api.dispatch(
            showNotification({ message: 'messages.error' }, 'error')
          );
          return { error: error as FetchBaseQueryError };
        }
      },
      keepUnusedDataFor: 1800,
      providesTags: [GET_BENEFIT_GROUP_DETAILS_API_TAG]
    }),
    getDefaultBenefitGroupsSettings: build.query<IBenefitDefaultSettings, void>(
      {
        async queryFn(_arg, api) {
          try {
            const data = await getDefaultBenefitGroupsSettings();
            const contractedBenefits = data?.contractedBenefits
              ? Object.entries(data?.contractedBenefits).map(
                  ([benefitType, values]) => {
                    const { taxFreeAmount, ...restValues } = values;
                    return {
                      benefitType,
                      ...restValues,
                      taxFreeAmount
                    };
                  }
                )
              : [];
            return {
              data: {
                lunchGlobalSettings: data.lunchGlobalSettings,
                contractedBenefits
              } as IBenefitDefaultSettings
            };
          } catch (error) {
            api.dispatch(
              showNotification({ message: 'messages.error' }, 'error')
            );
            return { error: error as FetchBaseQueryError };
          }
        },
        keepUnusedDataFor: 1800,
        providesTags: [GET_BENEFIT_GROUP_DEFAULT_SETTINGS_API_TAG]
      }
    ),
    createBenefitGroup: build.mutation<
      number,
      { payload: ICreateBenefitGroupForm }
    >({
      queryFn: ({ payload }) => createBenefitGroup(payload),
      invalidatesTags: [
        CREATE_BENEFIT_GROUP_API_TAG,
        GET_USER_DETAILS_API_TAG,
        GET_BENEFIT_GROUP_DEFAULT_SETTINGS_API_TAG,
        GET_BENEFIT_GROUP_DETAILS_API_TAG
      ]
    }),
    updateBenefitGroup: build.mutation<
      void,
      { groupId: string; payload: ICreateBenefitGroupForm }
    >({
      queryFn: ({ groupId, payload }) => updateBenefitGroup(groupId, payload),
      invalidatesTags: [
        CREATE_BENEFIT_GROUP_API_TAG,
        GET_USER_DETAILS_API_TAG,
        GET_BENEFIT_GROUP_DEFAULT_SETTINGS_API_TAG,
        GET_BENEFIT_GROUP_DETAILS_API_TAG
      ]
    }),
    changeBeneficiaryGroup: build.mutation<
      { affectedBeneficiaries: number },
      { personIds: string[]; newGroupId: number }
    >({
      queryFn: async ({ personIds, newGroupId }) => {
        const response = await changeBeneficiaryGroup(personIds, newGroupId);
        if (response.data) {
          return { data: response.data };
        }
        return { error: response.error as FetchBaseQueryError };
      },
      invalidatesTags: [CHANGE_BENEFICIARY_GROUP_API_TAG, ...BENEFICIARY_TAGS]
    }),
    deleteBenefitGroup: build.mutation<void, { groupId: string }>({
      queryFn: ({ groupId }) => deleteBenefitGroup(groupId),
      invalidatesTags: [CREATE_BENEFIT_GROUP_API_TAG]
    })
  })
});

export const {
  useGetBenefitGroupsQuery,
  useGetDefaultBenefitGroupsSettingsQuery,
  useCreateBenefitGroupMutation,
  useUpdateBenefitGroupMutation,
  useChangeBeneficiaryGroupMutation,
  useGetBenefitGroupDetailsQuery,
  useDeleteBenefitGroupMutation
} = benefitGroupsApi;
